import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { FollowingUser } from '@/types/residents/marketplaceListing';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { UserAvatar } from './shared';

const ChevronRightIcon = RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT;
const CheckIcon = RESIDENTS_SVG_COMPONENTS.CHECK;

interface FollowingUserCardProps {
  user: FollowingUser;
  onFollowToggle: (userId: string) => void;
  onProfilePress: (userId: string) => void;
}

export const FollowingUserCard: React.FC<FollowingUserCardProps> = ({
  user,
  onFollowToggle,
  onProfilePress,
}) => {
  const handleFollowPress = () => {
    onFollowToggle(user.id);
  };

  const handleCardPress = () => {
    onProfilePress(user.id);
  };

  return (
    <View style={styles.container}>
      {/* Top Row - Avatar and Follow Button */}
      <View style={styles.topRow}>
        <UserAvatar
          initials={user.initials}
          avatarUrl={user.avatarUrl}
          size="large"
        />
        
        <TouchableOpacity
          style={[
            styles.followButton,
            user.isFollowing && styles.followingButton,
          ]}
          onPress={handleFollowPress}
          activeOpacity={0.8}
        >
          {user.isFollowing && (
            <CheckIcon width={18} height={18} color={MARKETPLACE_COLORS.accentBlue} />
          )}
          <Text style={[
            styles.followButtonText,
            user.isFollowing && styles.followingButtonText,
          ]}>
            {user.isFollowing ? 'Following' : 'Follow'}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Bottom Row - Name and Navigation */}
      <TouchableOpacity 
        style={styles.bottomRow}
        onPress={handleCardPress}
        activeOpacity={0.8}
      >
        <Text style={styles.userName} numberOfLines={1}>
          {user.name}
        </Text>
        <View style={styles.navigationContainer}>
          {user.hasNewListing && (
            <View style={styles.newListingDot} />
          )}
          <ChevronRightIcon width={20} height={20} color={MARKETPLACE_COLORS.textMuted} />
        </View>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderRadius: 16,
    padding: 16,
    gap: 8,
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  followButton: {
    backgroundColor: MARKETPLACE_COLORS.accentBlueSolid,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  followingButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.accentBlueSolid,
  },
  followButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 18,
  },
  followingButtonText: {
    color: MARKETPLACE_COLORS.accentBlue,
  },
  bottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    gap: 20,
  },
  userName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  navigationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  newListingDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: MARKETPLACE_COLORS.productivityOrange,
  },
});

